import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getLayoutForPath } from "../../routes";

export interface LayoutConfig {
  heading: string;
  secondarySidebarVisible: boolean;
  addToFavoriteVisible?: boolean;
  backToVisible?: boolean;
  useToolVisible?: boolean;
  signUpForNewsVisible?: boolean;
}

const defaultLayoutConfig: LayoutConfig = {
  heading: "",
  secondarySidebarVisible: true,
};

type LayoutConfigState = {
  layoutConfig: LayoutConfig;
  setLayoutConfig: (layoutConfig: LayoutConfig) => void;
  moduleName: string;
};

const LayoutConfigContext = createContext<LayoutConfigState>({
  layoutConfig: defaultLayoutConfig,
  setLayoutConfig: () => {},
  moduleName: "",
});

export const useLayoutConfig = (): LayoutConfigState => {
  const layoutConfigState = useContext(LayoutConfigContext);
  if (!layoutConfigState) {
    throw new Error("useLoading must be used within a LayoutConfigProvider");
  }
  return layoutConfigState;
};

export default function LayoutConfigProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [layoutConfig, setLayoutConfig] = useState(defaultLayoutConfig);
  const [moduleName, setModuleName] = useState("");
  const location = useLocation();

  useEffect(() => {
    setLayoutConfig(getLayoutForPath(location.pathname));
    // Extract module name from path if it follows /modules/[moduleName] pattern
    const pathParts = location.pathname.split("/");
    if (pathParts[1] === "modules" && pathParts[2]) {
      setModuleName(pathParts[2]);
    } else {
      setModuleName("");
    }
  }, [location.pathname]);

  const layoutConfigState: LayoutConfigState = {
    layoutConfig,
    setLayoutConfig,
    moduleName,
  };

  return (
    <LayoutConfigContext.Provider value={layoutConfigState}>
      {children}
    </LayoutConfigContext.Provider>
  );
}